import { call, delay, fork, put, race, select, take, takeLatest } from 'redux-saga/effects';
import { push } from 'redux-first-history';
import { stringify } from 'query-string';
import { cloneDeep, filter } from 'lodash-es';
import {
    Campaign,
    CampaignStatus,
    CampaignTemplate,
    CampaignType,
    CampaignTypeFilter,
    ExportStatus,
    IntervalType,
} from '@dev/ee-api/ee-api-models';

import i18n from '@/../i18n';
import { store } from '@shared-store/shared-store';
import { safelyEncodeBase64Url } from '@dev/ee-tools';
import { CampaignsPaths, ExportsPaths, pathsParams } from '@/services/dictionaries/Paths';
import { ConfigState } from '@/store/config/types';
import { TotalEventHeaders } from '@/services/dictionaries/Headers';
import { extractStatusCounts, getDefaultSummaryReportToDate } from '@/services/tools/campaigns';
import { GetCampaignSimpleStatsAllDataSuccess } from '@/store/campaigns/simpleStats/action';

import { calculateOffset } from '@/services/tools/handleUrlParams';
import { getCampaignCreatorType } from '@/services/tools/getCampaignCreatorType';
import { Logger } from '@/services/errors/appLogger';
import dayjs from '../services/tools/dayjs';
import {
    BeginPollingCampaignSimpleStats,
    CampaignByTargetError,
    CampaignByTargetSuccess,
    CancelCampaignStart,
    CancelCampaignSuccess,
    ChangeAutoresponderStatus,
    ChangeAutoresponderStatusSuccess,
    CopyCampaignsByNameError,
    CopyCampaignsByNameSuccess,
    DeleteCampaignsSuccess,
    GetCampaignByNameSuccess,
    GetCampaignClicksAndOpensByName,
    GetCampaignClicksAndOpensByNameSuccess,
    GetCampaignReportsSummary,
    GetCampaigns,
    GetCampaignsError,
    GetCampaignSimpleStats,
    GetCampaignSimpleStatsSuccess,
    GetCampaignsSuccess,
    GetInProgressCampaignByChannelNameSuccess,
    GetLocationStatsByCountryIsoSuccess,
    LoadPreviewTemplates,
    LoadPreviewTemplatesError,
    LoadPreviewTemplatesSuccess,
    PollingCampaignSimpleStatsSuccess,
    SetAbSplitCurrentTemplate,
    SetTemplateVersionLabels,
    TemplateDeletedStatus,
    UpdateCampaignsPagination,
    getCampaignsListPagination,
    getCampaignsCounts,
    CampaignsNamesError,
    GetUpdateCampaignsNames,
} from '../store/campaigns/actions';
import {
    GetLinkTrackingAbSplitStart,
    GetLinkTrackingListStart,
    GetReportsSummaryError,
    GetReportsSummarySuccess,
} from '../store/reports/actions';
import {
    cancelCampaign,
    checkTaskStatus,
    copyCampaignsByName,
    deleteCampaigns,
    exportAutomationContacts,
    exportCampaigns,
    getCampaignClicksAndOpensStatsByName,
    getCampaigns,
    getCampaignNames,
    getCampaignSimpleStatsByName,
    getInProgressByChannelName,
    getLocationStatsByCountryIso,
    getReportsSummary,
    getSingleCampaignByName,
    loadTargetCampaigns,
    previewTemplate,
    ReportsSummaryParams,
    updateCampaign,
} from '../services/ElasticApi';
import { ShowDialog } from '../store/dialog/actions';
import { HideToast, ShowToast } from '../store/toast/actions';
import {
    BeginPollingCampaignSimpleStatsAction,
    CampaignInProgressAction,
    CampaignsActionTypes,
    CampaignsByTargetFetchStartAction,
    CampaignTemplateInterface,
    CancelCampaignAction,
    CancelCampaignStartAction,
    ChangeAutoresponderStatusAction,
    CopyCampaignsByNameStartAction,
    DeleteCampaignsStartAction,
    ExportAutomationContactsAction,
    ExportCampaignsAction,
    GetCampaignByNameAction,
    GetCampaignClicksAndOpensByNameAction,
    GetCampaignReportsSummaryAction,
    GetCampaignsAction,
    GetCampaignSimpleStatsAction,
    GetLocationStatsByCountryIsoStartAction,
    LoadPreviewTemplatesAction,
    PauseAutoresponderAction,
    PreviewCampaignAction,
    UrlSearchParams,
    GetCampaingsNamesAction,
} from '../store/campaigns/types';
import { DEFAULT_PER_PAGE_COUNT } from '../services/constants';
import {
    getCampaignStartDate,
    getCampaignTemplateNames,
} from '../components/Campaigns/CampaignReport/Overview/helperFunctions';
import { LONG_DATE_FORMAT, SHORT_DATE_FORMAT } from '../services/constants';
import { isNewAbSplit } from '../components/Campaigns/helperFunctions';

function* ShowErrorToast(e: any): any {
    Logger.error(e);
    yield put(
        ShowToast({
            severity: 'error',
            message: `Error: ${e?.response?.data?.Error ?? e.message}`,
            duration: 6000,
            onClose: HideToast,
        })
    );
}

function* getCampaignByTarget(action: CampaignsByTargetFetchStartAction): any {
    try {
        yield delay(500);
        const { params } = action.payload;
        const resp = yield call(loadTargetCampaigns, params.name, params.nameLike, params.limit, params.offset);
        const statusCounts = extractStatusCounts(resp.headers);
        const campaigns = resp.data;
        yield put(CampaignByTargetSuccess(campaigns, statusCounts));
    } catch (e) {
        yield ShowErrorToast(e);
        yield put(CampaignByTargetError(e));
    }
}

function* watchGetCampaignsByTarget(): any {
    yield takeLatest(CampaignsActionTypes.LOAD_BY_TARGET_START, getCampaignByTarget);
}

function* getCampaignsSaga(action: GetCampaignsAction): any {
    try {
        const {
            search,
            offset = 0,
            limit = DEFAULT_PER_PAGE_COUNT,
            from,
            to,
            status,
            type,
            isUrlUpdateDisabled,
            ...rest
        } = action.payload;

        const activePage = (offset + limit) / limit;
        const pagination = {
            activePage,
            search,
            perPage: limit,
            from,
            to,
            status,
            type,
        };

        yield put(UpdateCampaignsPagination(pagination));
        const response = yield call(getCampaigns, { search, offset, limit, status, from, to, type, ...rest });

        const collection = response.data;

        const statusCounts = extractStatusCounts(response.headers);

        if (!response) {
            throw Error;
        }

        const urlQueryParams: UrlSearchParams = {
            page: activePage,
            perPage: limit,
            ...(search ? { search } : {}),
            ...(from ? { from: dayjs(from).format(SHORT_DATE_FORMAT) } : {}),
            ...(to ? { to: dayjs(to).format(SHORT_DATE_FORMAT) } : {}),
            ...(status ? { status } : {}),
            ...(type ? { type } : {}),
        };

        yield put(GetCampaignsSuccess(collection, statusCounts));

        if (!isUrlUpdateDisabled) {
            yield put(push({ search: stringify(urlQueryParams as any) }));
        }
    } catch (e) {
        Logger.error(e);
        yield ShowErrorToast(e);
        yield put(GetCampaignsError());
    }
}

function* watchGetCampaigns(): any {
    yield takeLatest(CampaignsActionTypes.LOAD_ALL, getCampaignsSaga);
}

function* getCampaignsNames(action: GetCampaingsNamesAction): any {
    try {
        const campaignType = action.payload;
        const resp = yield call(getCampaignNames, campaignType);
        const campaignsNames = resp.data;
        yield put(GetUpdateCampaignsNames(campaignsNames));
    } catch (e) {
        yield ShowErrorToast(e);
        yield put(CampaignsNamesError());
    }
}

function* watchGetCampaignsNames(): any {
    yield takeLatest(CampaignsActionTypes.CAMPAIGNS_NAMES, getCampaignsNames);
}

function* deleteConditionsFulfilled(action: DeleteCampaignsStartAction): any {
    try {
        const { campaignNames, type } = action.payload;
        const pagination = yield select(getCampaignsListPagination);
        const statusCount = yield select(getCampaignsCounts);
        const countAfterDelete = statusCount.total - campaignNames.length;
        const selectedAll = statusCount.total === campaignNames.length;
        const offset = selectedAll ? 0 : calculateOffset(pagination.activePage, pagination.perPage, countAfterDelete);
        const status = selectedAll ? [] : pagination.status;

        const reloadPageParams = {
            search: pagination.search,
            limit: pagination.perPage,
            offset,
            from: pagination.from,
            to: pagination.to,
            status,
            type,
        };

        yield call(deleteCampaigns, campaignNames);

        yield put(DeleteCampaignsSuccess());

        yield put(GetCampaigns(reloadPageParams));

        const infoMessage = type.includes(CampaignTypeFilter.Draft)
            ? i18n.t('deleteddraftcampaigns')
            : i18n.t('deletedcampaigns');
        yield put(
            ShowToast({
                message: infoMessage,
                severity: 'info',
                duration: 2000,
            })
        );
    } catch (e) {
        Logger.error(e);
        yield ShowErrorToast(e);
    }
}

function* watchDeleteConditionsFullfilled(): any {
    yield takeLatest(CampaignsActionTypes.DELETE_CAMPAIGNS_START, deleteConditionsFulfilled);
}

function* pauseAutoresponderSaga(action: PauseAutoresponderAction): any {
    try {
        const { campaign } = action.payload;

        yield put(
            ShowDialog({
                title: i18n.t('pausecampaign'),
                cancelLabel: i18n.t('cancel'),
                body: i18n.t('campaigncpauseconfirmation', { campaignname: campaign.Name }),
                confirmLabel: i18n.t('pause'),
                onClickConfirm: (): void => {
                    store.dispatch(ChangeAutoresponderStatus(campaign, CampaignStatus.Paused));
                },
            })
        );
    } catch (e) {
        Logger.error(e);
        yield ShowErrorToast(e);
    }
}

function* watchPauseAutoresponder(): any {
    yield takeLatest(CampaignsActionTypes.PAUSE_AUTORESPONDER, pauseAutoresponderSaga);
}

function* reactivateAutoresponderSaga(action: PauseAutoresponderAction): any {
    try {
        const { campaign } = action.payload;

        yield put(
            ShowDialog({
                title: i18n.t('reactivatecampaign'),
                cancelLabel: i18n.t('cancel'),
                body: i18n.t('campaignreactivateconfirmation', { campaignname: campaign.Name }),
                confirmLabel: i18n.t('reactivate'),
                onClickConfirm: (): void => {
                    store.dispatch(ChangeAutoresponderStatus(campaign, CampaignStatus.Active));
                },
            })
        );
    } catch (e) {
        Logger.error(e);
        yield ShowErrorToast(e);
    }
}

function* watchReactivateAutoresponder(): any {
    yield takeLatest(CampaignsActionTypes.REACTIVATE_AUTORESPONDER, reactivateAutoresponderSaga);
}

//remove winner tempalte if it has no TemplateName as then api will fail
const removeWinnerTemplateFromCampaign = (templates: CampaignTemplate[]) =>
    templates?.filter((singleTemplate) => !(!singleTemplate.TemplateName && singleTemplate.IsWinner));

function* changeAutoresponderStatus(action: ChangeAutoresponderStatusAction): any {
    try {
        const { campaign, status } = action.payload;
        const newCampaign = cloneDeep(campaign);
        newCampaign.Status = status;
        newCampaign.CampaignTemplates = removeWinnerTemplateFromCampaign(newCampaign.CampaignTemplates);

        yield call(updateCampaign, newCampaign);

        yield put(ChangeAutoresponderStatusSuccess(newCampaign));
    } catch (e) {
        Logger.error(e);
        yield ShowErrorToast(e);
    }
}

function* watchPauseAutoresponderConfirmed(): any {
    yield takeLatest(CampaignsActionTypes.CHANGE_AUTORESPONDER_STATUS, changeAutoresponderStatus);
}

function* cancelCampaignSaga(action: CancelCampaignAction): any {
    try {
        const { campaign } = action.payload;

        yield put(
            ShowDialog({
                title: i18n.t('cancelsendingcampaign'),
                cancelLabel: i18n.t('cancel'),
                body: i18n.t('campaigncancelconfirmation', { campaignname: campaign.Name }),
                destructiveLabel: i18n.t('confirm'),
                onClickDestructive: (): void => {
                    store.dispatch(CancelCampaignStart(campaign));
                },
            })
        );
    } catch (e) {
        Logger.error(e);
        yield ShowErrorToast(e);
    }
}

function* watchCancelCampaign(): any {
    yield takeLatest(CampaignsActionTypes.CANCEL_CAMPAIGN, cancelCampaignSaga);
}

function* cancelCampaignSagaConfirmed(action: CancelCampaignStartAction): any {
    try {
        const { campaign } = action.payload;

        yield call(cancelCampaign, campaign.Name);

        yield put(CancelCampaignSuccess(campaign));

        yield put(
            ShowToast({
                message: i18n.t('campaigncancelled'),
                severity: 'info',
                duration: 2000,
            })
        );
    } catch (e) {
        Logger.error(e);
        yield ShowErrorToast(e);
    }
}

function* watchCancelCampaignConfirmed(): any {
    yield takeLatest(CampaignsActionTypes.CANCEL_CAMPAIGN_START, cancelCampaignSagaConfirmed);
}

function* previewCampaign(action: PreviewCampaignAction): any {
    try {
        const { campaign, isWithTestResult } = action.payload;
        const templateNames = getCampaignTemplateNames(campaign, isWithTestResult)?.filter(
            (item) => !!item.backendName
        );
        if (templateNames?.length) {
            yield put(LoadPreviewTemplates(templateNames));
        }
    } catch (e) {
        Logger.error(e);
        yield ShowErrorToast(e);
    }
}

function* watchPreviewCampaign(): any {
    yield takeLatest(CampaignsActionTypes.PREVIEW_CAMPAIGN, previewCampaign);
}

function* loadLocationStatsByCountryIso(action: GetLocationStatsByCountryIsoStartAction): any {
    const { payload } = action;

    try {
        const resp = yield call(getLocationStatsByCountryIso, payload);
        const totalCount = Number(resp.headers[TotalEventHeaders.TotalCount]) || 0;
        const response = { dataSetByCountry: resp.data, totalCount };

        const { offset, limit } = payload;
        const activePage = (offset + limit) / limit;

        const urlQueryParams: UrlSearchParams = {
            page: activePage,
            perPage: limit,
        };

        if (activePage) {
            yield put(push({ search: stringify(urlQueryParams as any) }));
        }

        yield put(GetLocationStatsByCountryIsoSuccess(response));
    } catch (e) {
        yield ShowErrorToast(e);
    }
}

function* watchLoadLocationStatsByCountryIso(): any {
    yield takeLatest(CampaignsActionTypes.GET_LOCATION_STATS_BY_COUNTRYISO_START, loadLocationStatsByCountryIso);
}

function* LoadPreviewTemplatesSaga(action: LoadPreviewTemplatesAction): any {
    try {
        const { names } = action.payload;

        const templates: CampaignTemplateInterface[] = [];

        let counter = 0;

        for (counter = 0; counter < names.length; counter++) {
            const html = yield call(previewTemplate, names[counter].backendName);
            templates.push({
                name: names[counter].displayName,
                backendName: names[counter].backendName,
                channelName: names[counter].channelName,
                html,
            });
        }

        yield put(LoadPreviewTemplatesSuccess(templates));
    } catch (e) {
        Logger.error(e);
        yield ShowErrorToast(e);
        yield put(LoadPreviewTemplatesError());
    }
}

function* watchLoadPreviewTemplates(): any {
    yield takeLatest(CampaignsActionTypes.LOAD_PREVIEW_TEMPLATES, LoadPreviewTemplatesSaga);
}

function* pollCampaignSimpleStatsByName(action?: BeginPollingCampaignSimpleStatsAction): any {
    while (true) {
        try {
            const {
                payload: { name, from, to },
            } = action;

            yield delay(5000);
            const response = yield call(getCampaignSimpleStatsByName, { name, from, to });

            yield put(PollingCampaignSimpleStatsSuccess(response.data));
        } catch (err) {
            Logger.error(err);
            yield ShowErrorToast(err);
        }
    }
}

function* watchPollingSimpleStatsByName(): any {
    while (true) {
        const pollAction = yield take(CampaignsActionTypes.BEGIN_POLLING_CAMPAIGN_SIMPLE_STATS);
        yield race([
            call(pollCampaignSimpleStatsByName, pollAction),
            take(CampaignsActionTypes.CANCEL_POLLING_CAMPAIGN_SIMPLE_STATS),
        ]);
    }
}

function* getInProgressCampaignByChannelName(action?: CampaignInProgressAction): any {
    try {
        const {
            payload: { channelName },
        } = action;

        const response = yield call(getInProgressByChannelName, channelName);
        yield put(GetInProgressCampaignByChannelNameSuccess(response.data));
    } catch (e) {
        yield ShowErrorToast(e);
    }
}

function* watchGetInProgressCampaignByChannelName(): any {
    yield takeLatest(CampaignsActionTypes.GET_IN_PROGRESS_CAMPAIGN_BY_CHANNEL_NAME, getInProgressCampaignByChannelName);
}

function* pollInProgressCampaignByChannelName(action?: CampaignInProgressAction): any {
    while (true) {
        try {
            const {
                payload: { channelName },
            } = action;

            yield delay(5000);

            const response = yield call(getInProgressByChannelName, channelName);

            yield put(GetInProgressCampaignByChannelNameSuccess(response.data));
        } catch (e) {
            yield ShowErrorToast(e);
        }
    }
}

function* watchPollingInProgressCampaignByChannelName(): any {
    while (true) {
        const pollAction = yield take(CampaignsActionTypes.POLLING_IN_PROGRESS_CAMPAIGN_BY_CHANNEL_NAME);
        yield race([
            call(pollInProgressCampaignByChannelName, pollAction),
            take(CampaignsActionTypes.CANCEL_POLLING_IN_PROGRESS_CAMPAIGN_BY_CHANNEL_NAME),
        ]);
    }
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function* loadCampaignByName(action: GetCampaignByNameAction): any {
    try {
        const {
            name,
            campaignType,
            isLoadReportsRequired,
            isLoadTemplatesRequired,
            isAbSplitClickAndOpenTestResultsRequired,
            isLoadLinkTrackingRequired,
            isSimpleStatsRequired,
            abSplitOpenAndClickTestResultsOptions,
            isLinkTrackingAbSplitRequired,
            isWithTestResult,
        } = action.payload;

        const responseCampaignByName = yield call(getSingleCampaignByName, name);
        const campaign: Campaign = responseCampaignByName.data;

        const isNewAbSplitCampaign = isNewAbSplit(campaign.CampaignTemplates);

        yield put(GetCampaignByNameSuccess(campaign));

        const from = campaign?.TriggerOptions?.DateToSendOn;
        const minutesFromStart = abSplitOpenAndClickTestResultsOptions?.minutesFromStart;
        let simpleStatsParams: { name: string; campaignType: CampaignType; from?: string; to?: string } = {
            name,
            campaignType,
        };

        if (campaign.Type === CampaignType.AbSplit) {
            yield put(SetTemplateVersionLabels(campaign.CampaignTemplates));
            yield put(SetAbSplitCurrentTemplate(campaign.CampaignTemplates[0].Name));

            const timeToAdd = campaign.SplitOptions.DetermineWinnerAfterXMinutes;
            const to = dayjs(from).add(timeToAdd, 'm').format(LONG_DATE_FORMAT);

            simpleStatsParams = { ...simpleStatsParams, from, to };
        }

        if (campaign.Status === CampaignStatus.Sending && campaignType === CampaignType.SendNow) {
            yield put(BeginPollingCampaignSimpleStats(campaign.Name));
        }

        if (isLoadReportsRequired && from) {
            yield put(GetCampaignReportsSummary(campaign));
        }

        if (isLinkTrackingAbSplitRequired) {
            const timeToAdd = campaign.SplitOptions.DetermineWinnerAfterXMinutes;
            const to = dayjs(from).add(timeToAdd, 'm').format(LONG_DATE_FORMAT);

            yield put(GetLinkTrackingAbSplitStart({ channelName: name, from, to }));
        }

        if (isLoadLinkTrackingRequired && from) {
            yield put(
                GetLinkTrackingListStart({
                    channelName: name,
                    from: dayjs(from).startOf('day').utc().format(LONG_DATE_FORMAT),
                    isRequiredFullDateRange: true,
                })
            );
        }

        if (isLoadTemplatesRequired) {
            const templateNames = getCampaignTemplateNames(campaign, isWithTestResult);
            // If the template hasn't been removed
            if (templateNames[0].backendName) {
                yield put(LoadPreviewTemplates(templateNames));
            } else {
                yield put(TemplateDeletedStatus());
            }
        }

        if (isAbSplitClickAndOpenTestResultsRequired) {
            const timeToAdd =
                minutesFromStart ||
                (isNewAbSplitCampaign ? campaign.SplitOptions.DetermineWinnerAfterXMinutes : 48 * 60);
            const to = dayjs(from).add(timeToAdd, 'm').format(LONG_DATE_FORMAT);

            yield put(
                GetCampaignClicksAndOpensByName({
                    interval: IntervalType.Hourly,
                    channelName: campaign.Name,
                    from,
                    to,
                })
            );
        }

        if (isSimpleStatsRequired) {
            yield put(GetCampaignSimpleStats(simpleStatsParams));
        }
    } catch (e) {
        yield ShowErrorToast(e);
    }
}

function* watchGetSendNowByNameAndReportsSummary(): any {
    yield takeLatest(CampaignsActionTypes.GET_CAMPAIGN_BY_NAME, loadCampaignByName);
}

function* loadCampaignSimpleStats(action: GetCampaignSimpleStatsAction): any {
    try {
        const { name, campaignType, from, to } = action.payload;

        const response = yield call(getCampaignSimpleStatsByName, { name, from, to });
        const campaign = response.data;

        if (campaignType !== CampaignType.AbSplit) {
            yield put(GetCampaignSimpleStatsSuccess(campaign));
        } else {
            const responseWithoutTo = yield call(getCampaignSimpleStatsByName, { name, from });
            const regularTemplatesStats = filter(
                campaign.CampaignTemplatesSummary,
                (singleStat) => !singleStat.IsWinner
            );
            const winnerTemplateStats = responseWithoutTo?.data?.CampaignTemplatesSummary?.filter(
                (singleStat) => singleStat.IsWinner
            );
            campaign.CampaignTemplatesSummary = [...regularTemplatesStats, ...winnerTemplateStats];

            yield put(GetCampaignSimpleStatsSuccess(campaign));
            yield put(GetCampaignSimpleStatsAllDataSuccess(responseWithoutTo.data));

            if (campaign.Status === CampaignStatus.Sending) {
                yield put(BeginPollingCampaignSimpleStats(name, from, to));
            }
        }
    } catch (e) {
        yield ShowErrorToast(e);
    }
}

function* watchGetCampaignsSimpleStats(): any {
    yield takeLatest(CampaignsActionTypes.GET_CAMPAIGN_SIMPLE_STATS, loadCampaignSimpleStats);
}

function* loadCampaignReportsSummary(action: GetCampaignReportsSummaryAction): any {
    try {
        const { campaign: actionCampaign, campaignName, from: actionFrom, to: actionTo, interval } = action.payload;
        let campaign = actionCampaign || store.getState().campaigns?.pickedCampaign?.campaign;

        if (!campaign) {
            const reponse = yield call(getSingleCampaignByName, campaignName);
            campaign = reponse.data;
        }

        const from = actionFrom || dayjs(getCampaignStartDate(campaign, LONG_DATE_FORMAT)).utc().format();
        const to = actionTo || getDefaultSummaryReportToDate(campaign);
        const reportsParams: ReportsSummaryParams = {
            from,
            to,
            interval: interval || IntervalType.Hourly,
            channelName: campaign?.Name,
        };

        const responseReportsSummary = yield call(getReportsSummary, reportsParams);

        yield put(
            push({
                search: stringify({
                    from: dayjs(from).format(SHORT_DATE_FORMAT),
                    to: dayjs(to).format(SHORT_DATE_FORMAT),
                }),
            })
        );

        yield put(GetReportsSummarySuccess(responseReportsSummary?.data));
    } catch (e) {
        Logger.error(e);
        yield ShowErrorToast(e);
        yield put(GetReportsSummaryError());
    }
}

function* watchGetCampaignReportsSummary(): any {
    yield takeLatest(CampaignsActionTypes.GET_CAMPAIGN_REPORTS_SUMMARY, loadCampaignReportsSummary);
}

function* getCampaignClicksAndOpensChartDataSaga(action: GetCampaignClicksAndOpensByNameAction): any {
    try {
        const response = yield call(getCampaignClicksAndOpensStatsByName, action.payload);

        yield put(GetCampaignClicksAndOpensByNameSuccess(response.data));
    } catch (e) {
        Logger.error(e);
        yield ShowErrorToast(e);
    }
}

function* watchGetCampaignClicksAndOpensChartDataSaga(): any {
    yield takeLatest(
        CampaignsActionTypes.GET_CAMPAIGN_CLICKS_AND_OPENS_CHART_DATA,
        getCampaignClicksAndOpensChartDataSaga
    );
}

function* exportCampaign(action: ExportCampaignsAction): any {
    const { payload } = action;
    let checkExportStatus = true;

    try {
        const resp = yield call(exportCampaigns, payload);

        while (checkExportStatus) {
            const statusResp = yield call(checkTaskStatus, resp.data.PublicExportID);
            const status: ExportStatus = statusResp.data;

            if (status === ExportStatus.Ready) {
                checkExportStatus = false;
            }

            if (status === ExportStatus.Error || status === ExportStatus.Expired) {
                throw new Error(statusResp);
            }

            delay(500);
        }

        yield put(
            ShowToast({
                severity: 'info',
                message: i18n.t('campaignsexported'),
                duration: 6000,
                onClose: HideToast,
            })
        );
        yield put(push(ExportsPaths.Exports));
    } catch (e) {
        yield ShowErrorToast(e);
    }
}
function* watchExportCampaigns(): any {
    yield takeLatest(CampaignsActionTypes.EXPORT_CAMPAIGNS, exportCampaign);
}

function* automationContacts(action: ExportAutomationContactsAction): any {
    const { payload } = action;

    let checkExportStatus = true;

    try {
        const resp = yield call(exportAutomationContacts, payload);

        while (checkExportStatus) {
            const statusResp = yield call(checkTaskStatus, resp.data.PublicExportID);
            const status: ExportStatus = statusResp.data;

            if (status === ExportStatus.Ready) {
                checkExportStatus = false;
            }

            if (status === ExportStatus.Error || status === ExportStatus.Expired) {
                throw new Error(statusResp);
            }

            delay(500);
        }

        yield put(
            ShowToast({
                severity: 'info',
                message: i18n.t('automationexported'),
                duration: 6000,
                onClose: HideToast,
            })
        );
        yield put(push(ExportsPaths.Exports));
    } catch (e) {
        yield ShowErrorToast(e);
    }
}
function* watchExportAutomationContacts(): any {
    yield takeLatest(CampaignsActionTypes.EXPORT_AUTOMATION_CONTACTS, automationContacts);
}

function* copyCampaign(action: CopyCampaignsByNameStartAction): any {
    try {
        const config = yield select((state): ConfigState => state.config);
        const { name } = action.payload;

        const copiedCampaign = yield call(copyCampaignsByName, name);
        yield put(CopyCampaignsByNameSuccess(copiedCampaign.data));
        const campaignCreatorType = getCampaignCreatorType(copiedCampaign.data.Type);
        config.isStandalone
            ? yield put(
                  push(
                      `${CampaignsPaths.CampaignCreatorName.replace(pathsParams.type, campaignCreatorType).replace(
                          pathsParams.name,
                          safelyEncodeBase64Url(copiedCampaign.data.Name)
                      )}`
                  )
              )
            : (window.location.hash = CampaignsPaths.OldViewCampaign.replace(
                  ':name',
                  safelyEncodeBase64Url(copiedCampaign.data.Name)
              ));
    } catch (e) {
        yield put(CopyCampaignsByNameError());
        yield ShowErrorToast(e);
    }
}
function* watchCopyCampaign(): any {
    yield takeLatest(CampaignsActionTypes.COPY_CAMPAIGNS_BY_NAME_START, copyCampaign);
}

const campaignSagas = [
    fork(watchGetCampaignsByTarget),
    fork(watchGetCampaigns),
    fork(watchPauseAutoresponder),
    fork(watchCancelCampaign),
    fork(watchDeleteConditionsFullfilled),
    fork(watchPauseAutoresponderConfirmed),
    fork(watchCancelCampaignConfirmed),
    fork(watchLoadPreviewTemplates),
    fork(watchPreviewCampaign),
    fork(watchPollingSimpleStatsByName),
    fork(watchGetSendNowByNameAndReportsSummary),
    fork(watchGetCampaignReportsSummary),
    fork(watchReactivateAutoresponder),
    fork(watchPollingInProgressCampaignByChannelName),
    fork(watchGetInProgressCampaignByChannelName),
    fork(watchGetCampaignClicksAndOpensChartDataSaga),
    fork(watchGetCampaignsSimpleStats),
    fork(watchExportCampaigns),
    fork(watchCopyCampaign),
    fork(watchLoadLocationStatsByCountryIso),
    fork(watchExportAutomationContacts),
    fork(watchGetCampaignsNames),
];

export default campaignSagas;
